import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../../shared/enums/request-status';

import {
  getDoNotCallListsRequest,
  createDoNotCallListRequest,
  updateDoNotCallListRequest,
  deleteDoNotCallListRequest,
  getDoNotCallListDetailsRequest,
  addPhoneNumberRequest,
  deletePhoneNumbersRequest,
  bulkDeletePhoneNumbersRequest,
  importPhoneNumbersViaCsvRequest,
} from './extra-actions';
import {
  DoNotCallList,
  DoNotCallListPhoneNumber,
  ImportDoNotCallListPhoneNumbersResponse,
  PaginationOptions,
} from '../../types/do-not-call-list';

type RequestState = {
  status: RequestStatus;
  message: string;
  error: any;
};

type BulkSelectedPhoneNumbers = {
  isAllPhoneNumbersSelected?: boolean;
  deSelectedPhoneNumberIds?: any[];
};

type State = {
  getDoNotCallListsRequest: RequestState;
  createDoNotCallListRequest: RequestState;
  updateDoNotCallListRequest: RequestState;
  deleteDoNotCallListRequest: RequestState;
  getDoNotCallListDetailsRequest: RequestState;
  addPhoneNumberRequest: RequestState;
  deletePhoneNumbersRequest: RequestState;
  bulkDeletePhoneNumbersRequest: RequestState;
  importPhoneNumbersViaCsvRequest: RequestState;
  doNotCallLists: DoNotCallList[];
  doNotCallListsPaginationOptions: PaginationOptions;
  doNotCallListDetailsPayload: DoNotCallList;
  doNotCallListDetails: DoNotCallListPhoneNumber[];
  doNotCallListDetailsPaginationOptions: PaginationOptions;
  createdDoNotCallListId: string;
  importPhoneNumbersResponse: ImportDoNotCallListPhoneNumbersResponse;
  bulkSelectedPhoneNumbers: BulkSelectedPhoneNumbers;
  selectedPhoneNumbers: DoNotCallListPhoneNumber[];
};

const initialState: State = {
  getDoNotCallListsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createDoNotCallListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateDoNotCallListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteDoNotCallListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getDoNotCallListDetailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addPhoneNumberRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deletePhoneNumbersRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  bulkDeletePhoneNumbersRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  importPhoneNumbersViaCsvRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  doNotCallLists: [],
  doNotCallListsPaginationOptions: null,
  doNotCallListDetails: [],
  doNotCallListDetailsPaginationOptions: null,
  createdDoNotCallListId: null,
  importPhoneNumbersResponse: null,
  bulkSelectedPhoneNumbers: null,
  selectedPhoneNumbers: [],
  doNotCallListDetailsPayload: null,
};

const doNotCallSlice = createSlice({
  name: 'callDoNotContact',
  initialState,
  reducers: {
    resetGetDoNotCallListsRequest: (state) => {
      state.getDoNotCallListsRequest = initialState.getDoNotCallListsRequest;
    },
    resetCreateDoNotCallListRequest: (state) => {
      state.createDoNotCallListRequest =
        initialState.createDoNotCallListRequest;
    },
    resetCreatedDoNotCallListId: (state) => {
      state.createdDoNotCallListId = initialState.createdDoNotCallListId;
    },
    resetUpdateDoNotCallListRequest: (state) => {
      state.updateDoNotCallListRequest =
        initialState.updateDoNotCallListRequest;
    },
    resetDeleteDoNotCallListRequest: (state) => {
      state.deleteDoNotCallListRequest =
        initialState.deleteDoNotCallListRequest;
    },
    resetGetDoNotCallListDetailsRequest: (state) => {
      state.getDoNotCallListDetailsRequest =
        initialState.getDoNotCallListDetailsRequest;
    },
    resetAddPhoneNumberRequest: (state) => {
      state.addPhoneNumberRequest = initialState.addPhoneNumberRequest;
    },
    resetDeletePhoneNumbersRequest: (state) => {
      state.deletePhoneNumbersRequest = initialState.deletePhoneNumbersRequest;
    },
    resetBulkDeletePhoneNumbersRequest: (state) => {
      state.bulkDeletePhoneNumbersRequest =
        initialState.bulkDeletePhoneNumbersRequest;
    },
    resetImportPhoneNumbersViaCsvRequest: (state) => {
      state.importPhoneNumbersViaCsvRequest =
        initialState.importPhoneNumbersViaCsvRequest;
    },

    // Bulk selection handlers
    onBulkSelectPhoneNumbers: (state, action) => {
      state.bulkSelectedPhoneNumbers = action.payload;
    },
    clearPhoneNumbersSelection: (state) => {
      state.bulkSelectedPhoneNumbers = initialState.bulkSelectedPhoneNumbers;
      state.selectedPhoneNumbers = initialState.selectedPhoneNumbers;
    },
    selectAllPhoneNumbers: (state, action) => {
      if (action.payload.status === true) {
        if (state.bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected) {
          action.payload.rows.forEach(({ id: rowId }) => {
            state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds = state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds.filter(
              (id) => rowId !== id,
            );
          });
        }
        state.selectedPhoneNumbers = state.selectedPhoneNumbers.concat(
          action.payload.rows,
        );
      } else {
        if (state.bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected) {
          action.payload.rows.forEach(({ id }) => {
            state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds = state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds.concat(
              [id],
            );
          });
        }
        state.selectedPhoneNumbers = state.selectedPhoneNumbers.reduce(
          (acc, row) => {
            let isPresent = false;
            action.payload.rows.forEach(({ id }) => {
              if (id === row.id) {
                isPresent = true;
              }
            });
            if (isPresent) {
              return acc;
            }
            return [...acc, row];
          },
          [],
        );
      }
    },
    selectSinglePhoneNumber: (state, action) => {
      if (action.payload.status === true) {
        if (state.bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected) {
          state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds = state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds.filter(
            (id) => id !== action.payload.row.id,
          );
        }
        state.selectedPhoneNumbers = state.selectedPhoneNumbers.concat([
          action.payload.row,
        ]);
      } else {
        if (state.bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected) {
          state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds = state.bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds.concat(
            [action.payload.row.id],
          );
        }
        state.selectedPhoneNumbers = state.selectedPhoneNumbers.filter(
          ({ id }) => id !== action.payload.row.id,
        );
      }
    },
    resetSelectedPhoneNumbers: (state) => {
      state.selectedPhoneNumbers = initialState.selectedPhoneNumbers;
    },
  },
  extraReducers: (builder) => {
    // Get Do Not Call Lists
    builder.addCase(getDoNotCallListsRequest.pending, (state) => {
      state.getDoNotCallListsRequest.status = RequestStatus.Pending;
      state.getDoNotCallListsRequest.error = null;
    });
    builder.addCase(getDoNotCallListsRequest.fulfilled, (state, action) => {
      state.getDoNotCallListsRequest.status = RequestStatus.Succeeded;
      state.getDoNotCallListsRequest.message = action.payload.message;
      const p = action.payload?.payload;
      state.doNotCallLists = p?.items ?? [];
      state.doNotCallListsPaginationOptions = p?.meta ?? null;
    });
    builder.addCase(getDoNotCallListsRequest.rejected, (state, action) => {
      state.getDoNotCallListsRequest.status = RequestStatus.Failed;
      state.getDoNotCallListsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Create Do Not Call List
    builder.addCase(createDoNotCallListRequest.pending, (state) => {
      state.createDoNotCallListRequest.status = RequestStatus.Pending;
      state.createDoNotCallListRequest.error = null;
    });
    builder.addCase(createDoNotCallListRequest.fulfilled, (state, action) => {
      state.createDoNotCallListRequest.status = RequestStatus.Succeeded;
      state.createDoNotCallListRequest.message = action.payload.message;
      state.createdDoNotCallListId = action.payload.payload?.id;
    });
    builder.addCase(createDoNotCallListRequest.rejected, (state, action) => {
      state.createDoNotCallListRequest.status = RequestStatus.Failed;
      state.createDoNotCallListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Do Not Call List
    builder.addCase(updateDoNotCallListRequest.pending, (state) => {
      state.updateDoNotCallListRequest.status = RequestStatus.Pending;
      state.updateDoNotCallListRequest.error = null;
    });
    builder.addCase(updateDoNotCallListRequest.fulfilled, (state, action) => {
      state.updateDoNotCallListRequest.status = RequestStatus.Succeeded;
      state.updateDoNotCallListRequest.message = action.payload.message;
    });
    builder.addCase(updateDoNotCallListRequest.rejected, (state, action) => {
      state.updateDoNotCallListRequest.status = RequestStatus.Failed;
      state.updateDoNotCallListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete Do Not Call List
    builder.addCase(deleteDoNotCallListRequest.pending, (state) => {
      state.deleteDoNotCallListRequest.status = RequestStatus.Pending;
      state.deleteDoNotCallListRequest.error = null;
    });
    builder.addCase(deleteDoNotCallListRequest.fulfilled, (state, action) => {
      state.deleteDoNotCallListRequest.status = RequestStatus.Succeeded;
      state.deleteDoNotCallListRequest.message = action.payload.message;
    });
    builder.addCase(deleteDoNotCallListRequest.rejected, (state, action) => {
      state.deleteDoNotCallListRequest.status = RequestStatus.Failed;
      state.deleteDoNotCallListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Do Not Call List Details
    builder.addCase(getDoNotCallListDetailsRequest.pending, (state) => {
      state.getDoNotCallListDetailsRequest.status = RequestStatus.Pending;
      state.getDoNotCallListDetailsRequest.error = null;
    });
    builder.addCase(
      getDoNotCallListDetailsRequest.fulfilled,
      (state, action) => {
        state.getDoNotCallListDetailsRequest.status = RequestStatus.Succeeded;
        state.getDoNotCallListDetailsRequest.message = action.payload.message;
        state.doNotCallListDetailsPayload = action.payload.payload ?? [];
        state.doNotCallListDetails = action.payload.payload.items ?? [];
        state.doNotCallListDetailsPaginationOptions =
          action.payload.payload.meta;
      },
    );
    builder.addCase(
      getDoNotCallListDetailsRequest.rejected,
      (state, action) => {
        state.getDoNotCallListDetailsRequest.status = RequestStatus.Failed;
        state.getDoNotCallListDetailsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Add Phone Number
    builder.addCase(addPhoneNumberRequest.pending, (state) => {
      state.addPhoneNumberRequest.status = RequestStatus.Pending;
      state.addPhoneNumberRequest.error = null;
    });
    builder.addCase(addPhoneNumberRequest.fulfilled, (state, action) => {
      state.addPhoneNumberRequest.status = RequestStatus.Succeeded;
      state.addPhoneNumberRequest.message = action.payload.message;
    });
    builder.addCase(addPhoneNumberRequest.rejected, (state, action) => {
      state.addPhoneNumberRequest.status = RequestStatus.Failed;
      state.addPhoneNumberRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete Phone Numbers
    builder.addCase(deletePhoneNumbersRequest.pending, (state) => {
      state.deletePhoneNumbersRequest.status = RequestStatus.Pending;
      state.deletePhoneNumbersRequest.error = null;
    });
    builder.addCase(deletePhoneNumbersRequest.fulfilled, (state, action) => {
      state.deletePhoneNumbersRequest.status = RequestStatus.Succeeded;
      state.deletePhoneNumbersRequest.message = action.payload.message;
    });
    builder.addCase(deletePhoneNumbersRequest.rejected, (state, action) => {
      state.deletePhoneNumbersRequest.status = RequestStatus.Failed;
      state.deletePhoneNumbersRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Bulk Delete Phone Numbers
    builder.addCase(bulkDeletePhoneNumbersRequest.pending, (state) => {
      state.bulkDeletePhoneNumbersRequest.status = RequestStatus.Pending;
      state.bulkDeletePhoneNumbersRequest.error = null;
    });
    builder.addCase(
      bulkDeletePhoneNumbersRequest.fulfilled,
      (state, action) => {
        state.bulkDeletePhoneNumbersRequest.status = RequestStatus.Succeeded;
        state.bulkDeletePhoneNumbersRequest.message = action.payload.message;
      },
    );
    builder.addCase(bulkDeletePhoneNumbersRequest.rejected, (state, action) => {
      state.bulkDeletePhoneNumbersRequest.status = RequestStatus.Failed;
      state.bulkDeletePhoneNumbersRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Import Phone Numbers via CSV
    builder.addCase(importPhoneNumbersViaCsvRequest.pending, (state) => {
      state.importPhoneNumbersViaCsvRequest.status = RequestStatus.Pending;
      state.importPhoneNumbersViaCsvRequest.error = null;
    });
    builder.addCase(
      importPhoneNumbersViaCsvRequest.fulfilled,
      (state, action) => {
        state.importPhoneNumbersViaCsvRequest.status = RequestStatus.Succeeded;
        state.importPhoneNumbersViaCsvRequest.message = action.payload.message;
        state.importPhoneNumbersResponse = action.payload.payload;
      },
    );
    builder.addCase(
      importPhoneNumbersViaCsvRequest.rejected,
      (state, action) => {
        state.importPhoneNumbersViaCsvRequest.status = RequestStatus.Failed;
        state.importPhoneNumbersViaCsvRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
  },
});

export const {
  resetGetDoNotCallListsRequest,
  resetCreateDoNotCallListRequest,
  resetCreatedDoNotCallListId,
  resetUpdateDoNotCallListRequest,
  resetDeleteDoNotCallListRequest,
  resetGetDoNotCallListDetailsRequest,
  resetAddPhoneNumberRequest,
  resetDeletePhoneNumbersRequest,
  resetBulkDeletePhoneNumbersRequest,
  resetImportPhoneNumbersViaCsvRequest,
  onBulkSelectPhoneNumbers,
  clearPhoneNumbersSelection,
  selectAllPhoneNumbers,
  selectSinglePhoneNumber,
  resetSelectedPhoneNumbers,
} = doNotCallSlice.actions;

export default doNotCallSlice.reducer;
